// The New button beside the Messages heading, and the small menu it opens: New chat,
// New group. Each choice is there only when the server said the person may do it
// (the page decides; see MessagesPage). With neither there is no button at all.
//
// The menu is the top bar's small glass panel. Escape, a click elsewhere and Tab close
// it; the arrow keys move between its choices.
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement, RefObject } from 'react';
import { MessageSquarePlus, SquarePen, UsersRound, type LucideIcon } from 'lucide-react';

export type NewChoice = 'chat' | 'group';

interface NewMenuProps {
  /** The person may start a one-to-one chat here. */
  chat: boolean;
  /** The person may create a group here. */
  group: boolean;
  onPick: (choice: NewChoice) => void;
  /** The New button, for the page to give focus back to when a pop-up closes. */
  buttonRef: RefObject<HTMLButtonElement | null>;
}

const CHOICES: Array<{ key: NewChoice; label: string; icon: LucideIcon }> = [
  { key: 'chat', label: 'New chat', icon: MessageSquarePlus },
  { key: 'group', label: 'New group', icon: UsersRound },
];

export default function NewMenu({ chat, group, onPick, buttonRef }: NewMenuProps): ReactElement | null {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const shown = CHOICES.filter((c) => (c.key === 'chat' ? chat : group));

  // A choice that goes while the menu is open (the server took it away) closes it.
  const none = shown.length === 0;
  useEffect(() => { if (none) setOpen(false); }, [none]);

  useEffect(() => {
    if (!open) return undefined;
    items.current[0]?.focus();
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, buttonRef]);

  if (none) return null;

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = items.current.findIndex((el) => el === document.activeElement);
    const last = shown.length - 1;
    const go = (i: number) => {
      e.preventDefault();
      items.current[i]?.focus();
    };
    if (e.key === 'ArrowDown') go(at >= last ? 0 : at + 1);
    else if (e.key === 'ArrowUp') go(at <= 0 ? last : at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(last);
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div ref={wrap} className="relative flex-none">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="New chat or group"
        title="Start a chat or a group"
        className="flex h-8 items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      >
        <SquarePen className="h-4 w-4" aria-hidden="true" />
        New
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="New"
          onKeyDown={onMenuKeyDown}
          className="glass-panel absolute right-0 top-full z-20 mt-2 w-44 rounded-2xl p-1.5"
        >
          {shown.map(({ key, label, icon: Icon }, i) => (
            <button
              key={key}
              ref={(el) => { items.current[i] = el; }}
              type="button"
              role="menuitem"
              aria-label={label}
              onClick={() => {
                setOpen(false);
                onPick(key);
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:text-primary-700 focus:outline-none"
            >
              <Icon className="h-4 w-4 text-primary-600" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
